import { Transaction, TransactionBatchCreateRequest, TransactionList } from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { TransactionClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

export type TransactionQueryFilters = EntityFilter<Transaction>;

export const useTransactionBatchGetQuery = <TSelectData = TransactionList,>({
  filters,
  enabled = true,
  select,
  ...queryOptions
}: {
  select?: (data: TransactionList) => TSelectData;
  filters: TransactionQueryFilters;
  enabled?: boolean;
} & Omit<
  UseQueryOptions<TransactionList, Error, TSelectData>,
  'queryKey' | 'queryFn' | 'enabled' | 'select'
>) => {
  return useQuery({
    queryKey: [
      queryKeys.transaction.root,
      queryKeys.transaction.list,
      queryKeys.transaction.getTransactionHash(filters),
    ],
    queryFn: async () => {
      const req = Transaction.create(filters);
      if (req.searchPhrase && req.searchPhrase !== '') {
        return await TransactionClientService.Search(req);
      } else {
        return await TransactionClientService.BatchGet(req);
      }
    },
    enabled,
    select,
    ...queryOptions,
  });
};

export const useMissingReceiptsQuery = ({
  userId,
  includeTransactionLineItems = false,
  includeStoredOcr = false,
  enabled = true,
}: {
  userId: number;
  includeTransactionLineItems?: boolean;
  includeStoredOcr?: boolean;
  enabled?: boolean;
}) => {
  const newTransactionsQuery = useTransactionBatchGetQuery({
    filters: {
      ownerId: userId,
      isActive: 1,
      includeStoredOcrForOwnerId: includeStoredOcr,
      statusId: 1,
      includeTransactionLineItems,
      withoutLimit: true,
    },
    enabled,
  });

  const rejectedTransactionsQuery = useTransactionBatchGetQuery({
    filters: {
      ownerId: userId,
      isActive: 1,
      includeStoredOcrForOwnerId: false,
      statusId: 4,
      includeTransactionLineItems,
      notEquals: ['VendorCategory'],
      vendorCategory: "'Receipt','PickTicket','Invoice'",
      withoutLimit: true,
    },
    enabled,
  });

  const assignedTransactionsQuery = useTransactionBatchGetQuery({
    filters: {
      assignedEmployeeId: userId,
      isActive: 1,
      notEquals: ['VendorCategory'],
      vendorCategory: "'Receipt','PickTicket','Invoice'",
      statusId: 1,
      includeTransactionLineItems,
      withoutLimit: true,
    },
    enabled,
  });

  const isPending =
    newTransactionsQuery.isPending ||
    rejectedTransactionsQuery.isPending ||
    assignedTransactionsQuery.isPending;

  const isError =
    newTransactionsQuery.isError ||
    rejectedTransactionsQuery.isError ||
    assignedTransactionsQuery.isError;

  const isSuccess =
    newTransactionsQuery.isSuccess &&
    rejectedTransactionsQuery.isSuccess &&
    assignedTransactionsQuery.isSuccess;

  const transactions = useMemo<Transaction[]>(() => {
    if (!isSuccess) return [];
    const newTxns = newTransactionsQuery.data?.results || [];
    const rejectedTxns = rejectedTransactionsQuery.data?.results || [];
    const assignedTxns = assignedTransactionsQuery.data?.results || [];

    const existingIds = new Set([...newTxns, ...rejectedTxns].map((txn) => txn.id));
    const uniqueAssignedTxns = assignedTxns.filter((txn) => !existingIds.has(txn.id));

    return [...newTxns, ...rejectedTxns, ...uniqueAssignedTxns].sort((a, b) => {
      const dateA = new Date(a.timestamp.split(' ').join('T'));
      const dateB = new Date(b.timestamp.split(' ').join('T'));
      return dateA.getTime() - dateB.getTime();
    });
  }, [
    isSuccess,
    newTransactionsQuery.data?.results,
    rejectedTransactionsQuery.data?.results,
    assignedTransactionsQuery.data?.results,
  ]);

  const isFetching =
    newTransactionsQuery.isFetching ||
    rejectedTransactionsQuery.isFetching ||
    assignedTransactionsQuery.isFetching;

  /** Loads all three lists again, for a Retry after an error. */
  const refetch = () =>
    Promise.all([
      newTransactionsQuery.refetch(),
      rejectedTransactionsQuery.refetch(),
      assignedTransactionsQuery.refetch(),
    ]);

  return {
    transactions,
    count: transactions.length,
    isPending,
    isError,
    isSuccess,
    isFetching,
    refetch,
  };
};

export const useTransactionGetQuery = <TSelectData = Transaction,>({
  filters,
  enabled = true,
  select,
}: {
  select?: (data: Transaction) => TSelectData;
  filters: TransactionQueryFilters;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [queryKeys.transaction.root, queryKeys.transaction.getTransactionHash(filters)],
    queryFn: async () => {
      const req = Transaction.create(filters);
      return await TransactionClientService.Get(req);
    },
    enabled,
    select,
  });
};

export const useTransactionUpdateMutation = ({
  updateCache = true,
  invalidateQueries = false,
}: { updateCache?: boolean; invalidateQueries?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (filters: Transaction) => {
      return await TransactionClientService.Update(filters);
    },
    onSuccess: async (updatedTransaction) => {
      queryClient.setQueriesData<TransactionList | Transaction>(
        { queryKey: [queryKeys.transaction.root] },
        (cache) => {
          if (cache && updateCache) {
            if (
              'results' in cache &&
              cache.results.find((cachedTxn) => cachedTxn.id === updatedTransaction.id)
            ) {
              console.log('Updating transaction in cache', updatedTransaction);
              const updatedResults = cache.results.map((txn) =>
                txn.id === updatedTransaction.id
                  ? Transaction.create({
                      ...updatedTransaction,
                      transactionLineItems: txn.transactionLineItems,
                    })
                  : txn,
              );

              return TransactionList.create({
                results: updatedResults,
                totalCount: cache.totalCount,
              });
            }

            // individual queries
            if ('id' in cache && cache.id === updatedTransaction.id) {
              return Transaction.create({
                ...updatedTransaction,
                transactionLineItems: cache.transactionLineItems,
              });
            }
            return cache;
          }
        },
      );
      queryClient.removeQueries({ queryKey: [queryKeys.transactionGet.root] });
      if (invalidateQueries) {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.transaction.root],
        });
      }
    },
  });
};

export const useTransactionCreateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (filters: Transaction) => {
      return await TransactionClientService.Create(filters);
    },
    onSuccess(data) {
      queryClient.setQueriesData<TransactionList>(
        { queryKey: [queryKeys.transaction.root, queryKeys.transaction.list] },
        (cache) => {
          if (cache) {
            if (!cache.results.find((txn) => txn.id === data.id)) {
              return TransactionList.create({
                results: [data, ...cache.results],
                totalCount: cache.totalCount + 1,
              });
            }
            return cache;
          }
        },
      );
      queryClient.invalidateQueries({
        queryKey: [queryKeys.transaction.root],
      });
      queryClient.removeQueries({ queryKey: [queryKeys.transactionGet.root] });
    },
  });
};

export const useTransactionDeleteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (filters: Transaction) => {
      return await TransactionClientService.Delete(filters);
    },
    onSuccess(_, deleted) {
      queryClient.setQueriesData<TransactionList>(
        { queryKey: [queryKeys.transaction.root, queryKeys.transaction.list] },
        (cache) => {
          if (cache) {
            if (cache.results.find((txn) => txn.id === deleted.id)) {
              return TransactionList.create({
                results: cache.results.filter((txn) => txn.id !== deleted.id),
                totalCount: cache.totalCount - 1,
              });
            }
            return cache;
          }
        },
      );
      queryClient.invalidateQueries({
        queryKey: [queryKeys.transaction.root],
      });
      queryClient.removeQueries({ queryKey: [queryKeys.transactionGet.root] });
    },
  });
};

export const useTransactionClientServiceGetQuery = ({
  enabled = true,
  filters,
}: {
  enabled?: boolean;
  filters: TransactionQueryFilters;
}) => {
  return useQuery({
    queryKey: [queryKeys.transactionGet.root, queryKeys.transactionGet.getFiltersHash(filters)],
    queryFn: async () => {
      const req = Transaction.create(filters);
      return await TransactionClientService.Get(req);
    },
    enabled,
  });
};

export const useTransactionImmediateGetQuery = () => {
  const queryClient = useQueryClient();
  const fetchData = useCallback(
    async (filters: TransactionQueryFilters) => {
      const cache = queryClient.getQueryData<Transaction>([
        queryKeys.transactionGet.root,
        queryKeys.transactionGet.getFiltersHash(filters),
      ]);
      if (cache) {
        return cache;
      }
      const req = Transaction.create(filters);
      const data = await TransactionClientService.Get(req);
      queryClient.setQueryData(
        [queryKeys.transactionGet.root, queryKeys.transactionGet.getFiltersHash(filters)],
        data,
      );
      return data;
    },
    [queryClient],
  );
  return fetchData;
};

export const useTransactionBatchCreateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (transactions: Transaction[]) => {
      return await TransactionClientService.BatchCreate(
        TransactionBatchCreateRequest.create({ transactions }),
      );
    },
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.transaction.root] });
    },
  });
};
